import React, { useState, useRef, useEffect } from 'react';
import {
  Camera, Film, Image, Video, Aperture, Focus,
  Package, BookOpen, Archive, Box, Layers, Tag,
  Star, Heart, Award, Gem, Crown, Sparkles,
  Music, Mic, Headphones,
  Mountain, Sun, Globe,
  Palette, Pen, Printer,
  Usb, HardDrive, Cpu,
} from 'lucide-react';

export const CATALOG_ICONS = [
  { name: 'Camera', Icon: Camera },
  { name: 'Film', Icon: Film },
  { name: 'Image', Icon: Image },
  { name: 'Video', Icon: Video },
  { name: 'Aperture', Icon: Aperture },
  { name: 'Focus', Icon: Focus },
  { name: 'Package', Icon: Package },
  { name: 'BookOpen', Icon: BookOpen },
  { name: 'Archive', Icon: Archive },
  { name: 'Box', Icon: Box },
  { name: 'Layers', Icon: Layers },
  { name: 'Tag', Icon: Tag },
  { name: 'Star', Icon: Star },
  { name: 'Heart', Icon: Heart },
  { name: 'Award', Icon: Award },
  { name: 'Gem', Icon: Gem },
  { name: 'Crown', Icon: Crown },
  { name: 'Sparkles', Icon: Sparkles },
  { name: 'Music', Icon: Music },
  { name: 'Mic', Icon: Mic },
  { name: 'Headphones', Icon: Headphones },
  { name: 'Mountain', Icon: Mountain },
  { name: 'Sun', Icon: Sun },
  { name: 'Globe', Icon: Globe },
  { name: 'Palette', Icon: Palette },
  { name: 'Pen', Icon: Pen },
  { name: 'Printer', Icon: Printer },
  { name: 'Usb', Icon: Usb },
  { name: 'HardDrive', Icon: HardDrive },
  { name: 'Cpu', Icon: Cpu },
];

export function IconPicker({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (iconName: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const selected = CATALOG_ICONS.find(i => i.name === value);
  const SelectedIcon = selected?.Icon || Package;

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      let left = rect.left;
      const dropdownWidth = 260;
      if (left + dropdownWidth > window.innerWidth) {
        left = Math.max(0, window.innerWidth - dropdownWidth - 8);
      }
      setDropdownPos({ top: rect.bottom + 4, left });
    }
    setOpen(!open);
  };

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} style={{ display: 'inline-block' }}>
      <button
        ref={triggerRef}
        onClick={handleOpen}
        style={{
          width: '40px',
          height: '40px',
          borderRadius: 'var(--radius-card)',
          background: 'var(--glass-bg-card)',
          border: '1px solid rgba(255,255,255,0.10)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--color-text)',
          transition: 'all 0.15s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'var(--glass-bg-card)';
        }}
        title="Seleziona icona"
      >
        <SelectedIcon size={18} />
      </button>

      {open && (
        <div
          style={{
            position: 'fixed',
            top: `${dropdownPos.top}px`,
            left: `${dropdownPos.left}px`,
            zIndex: 9999,
            background: 'var(--glass-bg-card)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 'var(--radius-card)',
            boxShadow: 'var(--shadow-lg)',
            padding: 'var(--space-3)',
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: 'var(--space-1)',
            width: 'max-content',
          }}
        >
          {CATALOG_ICONS.map(({ name, Icon }) => (
            <button
              key={name}
              onClick={() => {
                onChange(name);
                setOpen(false);
              }}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-card)',
                background: value === name ? 'var(--color-primary)' : 'var(--glass-bg-card)',
                border: '1px solid ' + (value === name ? 'var(--color-primary)' : 'transparent'),
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: value === name ? 'white' : 'var(--color-text)',
                transition: 'all 0.15s ease',
              }}
              onMouseEnter={(e) => {
                if (value !== name) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.02)';
                }
              }}
              onMouseLeave={(e) => {
                if (value !== name) {
                  e.currentTarget.style.background = 'var(--glass-bg-card)';
                }
              }}
              title={name}
            >
              <Icon size={18} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
